import { el } from '../utils/dom.js';

/**
 * Tile collision editor — define collision shapes per tile.
 * Supports rectangle and polygon shapes drawn on a zoomed-in tile canvas.
 * Shapes stored on EditorState as `tileCollisions: Map<globalTileId, [{type, ...}]>`.
 *
 * This is per-TILE collision (inherent to the tileset — "this tile type always has this hitbox").
 * Compare with PropertyBrush which paints per-CELL collision (specific to one map instance).
 * Both approaches are valid and complementary. Most engines use per-tile shapes from the tileset.
 */
export class TileCollisionEditor {
  constructor(state, mapCanvas, compositor) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
  }

  show(globalTileId) {
    const ts = this.state.getTilesetForGid(globalTileId);
    if (!ts) return;

    const tileCollisions = this.state.get('tileCollisions') || new Map();
    const existing = tileCollisions.get(globalTileId) || [];
    const shapes = existing.map(s => JSON.parse(JSON.stringify(s))); // Deep clone

    const ZOOM = 8;
    const canvasW = ts.tileWidth * ZOOM;
    const canvasH = ts.tileHeight * ZOOM;

    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) overlay.remove();
    }});
    const dialog = el('div', { className: 'dialog', style: { maxWidth: '600px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, `Tile Collision — Tile ${globalTileId}`));

    // Info banner
    dialog.appendChild(el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', marginBottom: '8px', lineHeight: '1.4' },
    }, 'Per-tile collision shapes (stored on tileset). These define the inherent hitbox for this tile type. ' +
       'For per-cell collision flags on specific map locations, use the Property Brush tool instead.'));

    // Mode selector
    let drawMode = 'rect'; // 'rect' or 'polygon'
    const modeRow = el('div', { style: { display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' } });
    modeRow.appendChild(el('span', { style: { fontSize: '12px' } }, 'Draw mode:'));
    const rectModeBtn = el('button', { className: 'btn btn-sm btn-primary', onClick: () => {
      drawMode = 'rect';
      rectModeBtn.className = 'btn btn-sm btn-primary';
      polyModeBtn.className = 'btn btn-sm';
    }}, 'Rectangle');
    const polyModeBtn = el('button', { className: 'btn btn-sm', onClick: () => {
      drawMode = 'polygon';
      rectModeBtn.className = 'btn btn-sm';
      polyModeBtn.className = 'btn btn-sm btn-primary';
    }}, 'Polygon');
    const fullTileBtn = el('button', { className: 'btn btn-sm', onClick: () => {
      shapes.push({ type: 'rect', x: 0, y: 0, width: ts.tileWidth, height: ts.tileHeight });
      renderShapeList();
      drawCanvas();
    }}, 'Full Tile');
    modeRow.append(rectModeBtn, polyModeBtn, fullTileBtn);
    dialog.appendChild(modeRow);

    // Canvas
    const canvas = el('canvas', {
      width: canvasW,
      height: canvasH,
      style: {
        border: '1px solid var(--border)',
        imageRendering: 'pixelated',
        cursor: 'crosshair',
        background: '#1a1a2e',
        display: 'block',
        margin: '0 auto 8px auto',
      },
    });
    const ctx = canvas.getContext('2d');
    dialog.appendChild(canvas);

    // Draw the tile + shapes
    const drawCanvas = () => {
      ctx.clearRect(0, 0, canvasW, canvasH);

      // Draw tile
      const rect = this.state.getTileRect(globalTileId);
      if (rect) {
        const img = this.state.get('tilesetImages').get(rect.tileset.image);
        if (img) {
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(img, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, canvasW, canvasH);
        }
      }

      // Draw grid
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth = 1;
      for (let x = 0; x <= canvasW; x += ZOOM) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasH);
        ctx.stroke();
      }
      for (let y = 0; y <= canvasH; y += ZOOM) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasW, y);
        ctx.stroke();
      }

      // Draw shapes
      for (let i = 0; i < shapes.length; i++) {
        const shape = shapes[i];
        const isSelected = i === selectedShape;
        ctx.strokeStyle = isSelected ? '#ffff00' : '#ff4444';
        ctx.fillStyle = isSelected ? 'rgba(255,255,0,0.2)' : 'rgba(255,0,0,0.2)';
        ctx.lineWidth = 2;

        if (shape.type === 'rect') {
          ctx.fillRect(shape.x * ZOOM, shape.y * ZOOM, shape.width * ZOOM, shape.height * ZOOM);
          ctx.strokeRect(shape.x * ZOOM, shape.y * ZOOM, shape.width * ZOOM, shape.height * ZOOM);
        } else if (shape.type === 'polygon') {
          ctx.beginPath();
          const pts = shape.points;
          if (pts.length > 0) {
            ctx.moveTo((shape.x + pts[0].x) * ZOOM, (shape.y + pts[0].y) * ZOOM);
            for (let p = 1; p < pts.length; p++) {
              ctx.lineTo((shape.x + pts[p].x) * ZOOM, (shape.y + pts[p].y) * ZOOM);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
          }
        }
      }

      // Draw in-progress rect
      if (dragging && drawMode === 'rect') {
        ctx.strokeStyle = '#00ff00';
        ctx.fillStyle = 'rgba(0,255,0,0.15)';
        ctx.lineWidth = 2;
        const x = Math.min(dragStart.x, dragEnd.x);
        const y = Math.min(dragStart.y, dragEnd.y);
        const w = Math.abs(dragEnd.x - dragStart.x);
        const h = Math.abs(dragEnd.y - dragStart.y);
        ctx.fillRect(x * ZOOM, y * ZOOM, w * ZOOM, h * ZOOM);
        ctx.strokeRect(x * ZOOM, y * ZOOM, w * ZOOM, h * ZOOM);
      }

      // Draw in-progress polygon
      if (polyPoints.length > 0 && drawMode === 'polygon') {
        ctx.strokeStyle = '#00ff00';
        ctx.fillStyle = 'rgba(0,255,0,0.15)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(polyPoints[0].x * ZOOM, polyPoints[0].y * ZOOM);
        for (let p = 1; p < polyPoints.length; p++) {
          ctx.lineTo(polyPoints[p].x * ZOOM, polyPoints[p].y * ZOOM);
        }
        ctx.stroke();

        // Draw points
        for (const pt of polyPoints) {
          ctx.fillStyle = '#00ff00';
          ctx.fillRect(pt.x * ZOOM - 3, pt.y * ZOOM - 3, 6, 6);
        }
      }
    };

    // Interaction state
    let dragging = false;
    let dragStart = { x: 0, y: 0 };
    let dragEnd = { x: 0, y: 0 };
    let polyPoints = [];
    let selectedShape = -1;

    const getTileCoord = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = Math.round((e.clientX - rect.left) / ZOOM);
      const y = Math.round((e.clientY - rect.top) / ZOOM);
      return { x: Math.max(0, Math.min(ts.tileWidth, x)), y: Math.max(0, Math.min(ts.tileHeight, y)) };
    };

    canvas.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      const coord = getTileCoord(e);

      if (drawMode === 'rect') {
        dragging = true;
        dragStart = coord;
        dragEnd = coord;
      } else if (drawMode === 'polygon') {
        // Check if clicking near first point to close polygon
        if (polyPoints.length >= 3) {
          const first = polyPoints[0];
          const dist = Math.sqrt((coord.x - first.x) ** 2 + (coord.y - first.y) ** 2);
          if (dist < 2) {
            // Close polygon
            const minX = Math.min(...polyPoints.map(p => p.x));
            const minY = Math.min(...polyPoints.map(p => p.y));
            shapes.push({
              type: 'polygon',
              x: minX,
              y: minY,
              points: polyPoints.map(p => ({ x: p.x - minX, y: p.y - minY })),
            });
            polyPoints = [];
            renderShapeList();
            drawCanvas();
            return;
          }
        }
        polyPoints.push(coord);
        drawCanvas();
      }
    });

    canvas.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      dragEnd = getTileCoord(e);
      drawCanvas();
    });

    canvas.addEventListener('mouseup', (e) => {
      if (!dragging || drawMode !== 'rect') return;
      dragging = false;
      dragEnd = getTileCoord(e);
      const x = Math.min(dragStart.x, dragEnd.x);
      const y = Math.min(dragStart.y, dragEnd.y);
      const w = Math.abs(dragEnd.x - dragStart.x);
      const h = Math.abs(dragEnd.y - dragStart.y);
      if (w > 0 && h > 0) {
        shapes.push({ type: 'rect', x, y, width: w, height: h });
        renderShapeList();
      }
      drawCanvas();
    });

    // Right-click to cancel polygon
    canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (polyPoints.length > 0) {
        polyPoints = [];
        drawCanvas();
      }
    });

    // Shape list
    const shapeListEl = el('div', { style: { maxHeight: '120px', overflowY: 'auto', marginBottom: '8px' } });
    dialog.appendChild(shapeListEl);

    const renderShapeList = () => {
      shapeListEl.innerHTML = '';
      if (shapes.length === 0) {
        shapeListEl.appendChild(el('div', { style: { color: 'var(--text-dim)', fontSize: '11px', padding: '4px' } },
          'No collision shapes. Draw rectangles or polygons on the tile above.'));
        return;
      }
      shapes.forEach((shape, i) => {
        const row = el('div', {
          style: {
            display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0',
            borderBottom: '1px solid var(--border)', cursor: 'pointer',
            background: i === selectedShape ? 'rgba(255,255,0,0.1)' : 'transparent',
          },
          onClick: () => {
            selectedShape = selectedShape === i ? -1 : i;
            renderShapeList();
            drawCanvas();
          },
        });

        let desc;
        if (shape.type === 'rect') {
          desc = `Rect (${shape.x}, ${shape.y}) ${shape.width}x${shape.height}`;
        } else {
          desc = `Polygon (${shape.points.length} points)`;
        }
        row.appendChild(el('span', { style: { fontSize: '11px', flex: '1' } }, desc));

        row.appendChild(el('button', { className: 'btn btn-sm', style: { color: '#f55' }, onClick: (e) => {
          e.stopPropagation();
          shapes.splice(i, 1);
          if (selectedShape >= shapes.length) selectedShape = -1;
          renderShapeList();
          drawCanvas();
        }}, '\u00d7'));

        shapeListEl.appendChild(row);
      });
    };
    renderShapeList();

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const saveBtn = el('button', { className: 'btn btn-primary', onClick: () => {
      if (shapes.length > 0) {
        tileCollisions.set(globalTileId, shapes);
      } else {
        tileCollisions.delete(globalTileId);
      }
      this.state.set('tileCollisions', tileCollisions);
      this.mapCanvas.invalidateAll();
      this.compositor.requestRender();
      overlay.remove();
    }}, 'Save');
    const clearBtn = el('button', { className: 'btn', style: { color: '#f55' }, onClick: () => {
      shapes.length = 0;
      selectedShape = -1;
      renderShapeList();
      drawCanvas();
    }}, 'Clear All');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(saveBtn, clearBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Initial draw
    drawCanvas();
  }
}
